import { useEffect, useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { GraduationCap } from "lucide-react";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { Breadcrumbs } from "../components/layout/Breadcrumbs";
import { PageTitle } from "../components/layout/PageTitle";
import { RoleGate } from "../components/RoleGate";
import { LoadingSpinner } from "../components/LoadingSpinner";
import { ErrorState } from "../components/ErrorState";
import { EmptyState } from "../components/EmptyState";
import { Modal } from "../components/Modal";
import { listCourses, createCourse, updateCourse, type CourseInput } from "../services/course.service";
import { getApiErrorMessage } from "../lib/apiClient";
import type { Course } from "../types";

interface CourseFormState {
  name: string;
  description: string;
  ageMin: string;
  ageMax: string;
}

const EMPTY_FORM: CourseFormState = { name: "", description: "", ageMin: "", ageMax: "" };

function CourseFormModal({
  open,
  onClose,
  initial,
  onSubmit,
  isSubmitting,
}: {
  open: boolean;
  onClose: () => void;
  initial?: Course | null;
  onSubmit: (input: CourseInput) => void;
  isSubmitting: boolean;
}): JSX.Element {
  const [form, setForm] = useState<CourseFormState>(EMPTY_FORM);

  useEffect(() => {
    if (!open) return;
    setForm(
      initial
        ? {
            name: initial.name,
            description: initial.description ?? "",
            ageMin: String(initial.ageMin),
            ageMax: String(initial.ageMax),
          }
        : EMPTY_FORM,
    );
  }, [open, initial]);

  const handleSubmit = (e: FormEvent): void => {
    e.preventDefault();
    onSubmit({
      name: form.name,
      description: form.description || null,
      ageMin: Number(form.ageMin),
      ageMax: Number(form.ageMax),
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={initial ? "Edit Course" : "New Course"}
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form="course-form" className="btn-primary" disabled={isSubmitting}>
            {isSubmitting ? "Saving..." : "Save"}
          </button>
        </>
      }
    >
      <form id="course-form" onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="label" htmlFor="name">
            Course name
          </label>
          <input
            id="name"
            className="input"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
            minLength={2}
          />
        </div>
        <div>
          <label className="label" htmlFor="description">
            Description
          </label>
          <textarea
            id="description"
            className="input"
            rows={3}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="label" htmlFor="ageMin">
              Min age
            </label>
            <input
              id="ageMin"
              type="number"
              className="input"
              value={form.ageMin}
              onChange={(e) => setForm({ ...form, ageMin: e.target.value })}
              required
              min={0}
            />
          </div>
          <div>
            <label className="label" htmlFor="ageMax">
              Max age
            </label>
            <input
              id="ageMax"
              type="number"
              className="input"
              value={form.ageMax}
              onChange={(e) => setForm({ ...form, ageMax: e.target.value })}
              required
              min={0}
            />
          </div>
        </div>
      </form>
    </Modal>
  );
}

function CoursesPageContent(): JSX.Element {
  const queryClient = useQueryClient();
  const { data, isLoading, isError, error } = useQuery({ queryKey: ["courses"], queryFn: listCourses });
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCourse, setEditingCourse] = useState<Course | null>(null);

  const createMutation = useMutation({
    mutationFn: createCourse,
    onSuccess: () => {
      toast.success("Course created");
      queryClient.invalidateQueries({ queryKey: ["courses"] });
      setModalOpen(false);
    },
    onError: (err) => toast.error(getApiErrorMessage(err)),
  });

  const updateMutation = useMutation({
    mutationFn: (input: CourseInput) => updateCourse(editingCourse!.id, input),
    onSuccess: () => {
      toast.success("Course updated");
      queryClient.invalidateQueries({ queryKey: ["courses"] });
      setModalOpen(false);
      setEditingCourse(null);
    },
    onError: (err) => toast.error(getApiErrorMessage(err)),
  });

  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) => updateCourse(id, { isActive }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["courses"] });
    },
    onError: (err) => toast.error(getApiErrorMessage(err)),
  });

  return (
    <DashboardLayout>
      <Breadcrumbs items={[{ label: "Courses" }]} />
      <div className="flex items-center justify-between">
        <PageTitle icon={GraduationCap} title="Courses" />
        <button
          type="button"
          className="btn-primary"
          onClick={() => {
            setEditingCourse(null);
            setModalOpen(true);
          }}
        >
          New Course
        </button>
      </div>

      <div className="mt-6">
        {isLoading && <LoadingSpinner />}
        {isError && <ErrorState message={getApiErrorMessage(error)} />}
        {data && data.length === 0 && (
          <EmptyState title="No courses yet" description="Create your first course to start scheduling events." />
        )}
        {data && data.length > 0 && (
          <div className="card overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-700">
              <thead className="bg-slate-50 dark:bg-slate-700/60">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-slate-500 dark:text-slate-400">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-slate-500 dark:text-slate-400">Age Range</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-slate-500 dark:text-slate-400">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase text-slate-500 dark:text-slate-400">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                {data.map((course) => (
                  <tr key={course.id} className="transition-colors duration-100 hover:bg-slate-100 dark:hover:bg-slate-700/60">
                    <td className="px-4 py-3">
                      <p className="font-medium text-slate-900 dark:text-slate-100">{course.name}</p>
                      {course.description && <p className="text-sm text-slate-500 dark:text-slate-400">{course.description}</p>}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-300">
                      {course.ageMin}-{course.ageMax}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`badge ${course.isActive ? "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300" : "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300"}`}>
                        {course.isActive ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        className="text-sm font-medium text-brand-600 hover:text-brand-800 dark:text-brand-400 dark:hover:text-brand-300 transition-colors duration-150"
                        onClick={() => {
                          setEditingCourse(course);
                          setModalOpen(true);
                        }}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="ml-4 text-sm font-medium text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 transition-colors duration-150"
                        onClick={() => toggleActiveMutation.mutate({ id: course.id, isActive: !course.isActive })}
                      >
                        {course.isActive ? "Deactivate" : "Activate"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <CourseFormModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditingCourse(null);
        }}
        initial={editingCourse}
        isSubmitting={createMutation.isPending || updateMutation.isPending}
        onSubmit={(input) => (editingCourse ? updateMutation.mutate(input) : createMutation.mutate(input))}
      />
    </DashboardLayout>
  );
}

export default function CoursesPage(): JSX.Element {
  return (
    <RoleGate roles={["SUPER_ADMIN"]}>
      <CoursesPageContent />
    </RoleGate>
  );
}
